// Pricing · Quotes — the deal-desk pipeline + approval queue. Reads GET /quotes;
// approve/reject (logged reason → audit) for quotes awaiting approval.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader, Card, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { Pill, TierPill } from '@/components/shared/badges';
import { EmptyState } from '@/components/shared/EmptyState';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { ReasonDialog } from '@/components/shared/ReasonDialog';
import { toast } from '@/components/shared/ToastProvider';
import { apiRequest } from '@/lib/supabase';
import { getPriceBook } from '@/lib/api/pricing';
import { PERSONA } from '@/lib/persona';
import { money } from '@/lib/format';

const SALES = PERSONA.sales.accent;

interface Quote {
  id: string;
  quote_number?: string;
  organization_name?: string | null;
  organization_id?: string | null;
  tier?: string;
  currency?: string;
  list_price_monthly?: number;
  quoted_price_monthly?: number;
  discount_pct?: number;
  fulfillment?: string;
  po_terms?: string | null;
  status?: string;
  price_version?: string;
}

const QS: Record<string, { bg: string; fg: string }> = {
  approved: { bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)' },
  'pending-approval': { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)' },
  'needs-approval': { bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)' },
  provisioned: { bg: 'var(--ps-pill-blue-bg)', fg: 'var(--ps-pill-blue-tx)' },
  rejected: { bg: 'var(--ps-pill-gray-bg)', fg: 'var(--ps-pill-gray-tx)' },
  draft: { bg: 'var(--ps-pill-gray-bg)', fg: 'var(--ps-pill-gray-tx)' },
};
const PENDING = new Set(['pending-approval', 'needs-approval']);

export function QuotesList() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [activeVersion, setActiveVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getPriceBook().then((pb) => setActiveVersion(pb.version)).catch(() => setActiveVersion(null));
    apiRequest<{ data?: Quote[] } | Quote[]>('/quotes')
      .then((r) => setQuotes(Array.isArray(r) ? r : r.data || []))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id: string) => {
    setBusy(true);
    try {
      await apiRequest(`/quotes/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      toast({ kind: 'success', title: 'Quote approved', body: 'Logged to the audit trail.' });
      load();
    } catch (e) {
      toast({ kind: 'error', title: 'Approve failed', body: e instanceof Error ? e.message : 'Could not approve.' });
    } finally { setBusy(false); }
  };
  const reject = async (reason: string) => {
    if (!rejectId) return;
    try {
      await apiRequest(`/quotes/${rejectId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
      toast({ kind: 'success', title: 'Quote rejected', body: 'Reason logged to the audit trail.' });
      setRejectId(null);
      load();
    } catch (e) {
      toast({ kind: 'error', title: 'Reject failed', body: e instanceof Error ? e.message : 'Could not reject.' });
    }
  };

  return (
    <div>
      <PageHeader title="Quotes" subtitle="Deal-desk pipeline — validated against the rate card"
        actions={<Btn kind="primary" accent={SALES} icon="file-plus-2" onClick={() => navigate('/sales/quotes/new')}>New Quote</Btn>} />
      <Card style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : quotes.length === 0 ? (
          <EmptyState icon="file-text" title="No quotes yet" body="Build a quote in the Quote Builder — configure, price-check against the rate card, then submit for approval or provision directly." action={() => navigate('/sales/quotes/new')} actionLabel="New Quote" />
        ) : (
          <Table cols={[{ label: 'Quote' }, { label: 'Account' }, { label: 'Tier' }, { label: 'List', align: 'right' }, { label: 'Quoted', align: 'right' }, { label: 'Discount' }, { label: 'Fulfillment' }, { label: 'Pricing' }, { label: 'Status' }, { label: '', align: 'right' }]}>
            {quotes.map((q) => {
              const pending = PENDING.has(q.status || '');
              const open = pending || q.status === 'draft';
              const stale = open && !!activeVersion && !!q.price_version && q.price_version !== activeVersion;
              return (
                <Row key={q.id} onClick={() => navigate(`/sales/quotes/${q.id}`)}>
                  <Td><Mono style={{ fontWeight: 600 }}>{q.quote_number || q.id}</Mono></Td>
                  <Td><CompanyChip id={q.organization_id} name={q.organization_name} origin={{ type: 'quote' }} icon={false} /></Td>
                  <Td>{q.tier ? <TierPill tier={q.tier} /> : '—'}</Td>
                  <Td align="right" style={{ color: 'var(--text-secondary)' }}>{q.list_price_monthly != null ? money(q.list_price_monthly) : '—'}</Td>
                  <Td align="right" style={{ fontWeight: 700 }}>{q.quoted_price_monthly != null ? money(q.quoted_price_monthly) : '—'}</Td>
                  <Td>{q.discount_pct ? <Pill bg="var(--ps-pill-amber-bg)" fg="var(--ps-pill-amber-tx)">-{Math.round(q.discount_pct)}%</Pill> : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>list</span>}</Td>
                  <Td>{q.fulfillment === 'po' ? <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">PO{q.po_terms ? ` · ${q.po_terms}` : ''}</Pill> : <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">Stripe</Pill>}</Td>
                  <Td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {q.price_version ? <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">{q.price_version}</Pill> : '—'}
                      {stale && <Pill bg="var(--ps-pill-amber-bg)" fg="var(--ps-pill-amber-tx)">stale</Pill>}
                    </span>
                  </Td>
                  <Td><Pill bg={(QS[q.status || 'draft'] || QS.draft).bg} fg={(QS[q.status || 'draft'] || QS.draft).fg}>{(q.status || 'draft').replace('-', ' ')}</Pill></Td>
                  <Td align="right" onClick={(e) => e.stopPropagation()}>
                    {pending ? (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <Btn sm kind="primary" accent="#16A34A" icon="check" disabled={busy} onClick={() => approve(q.id)}>Approve</Btn>
                        <Btn sm icon="x" disabled={busy} onClick={() => setRejectId(q.id)} style={{ color: 'var(--ps-pill-red-tx)', borderColor: 'var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)' }}>Reject</Btn>
                      </div>
                    ) : null}
                  </Td>
                </Row>
              );
            })}
          </Table>
        )}
      </Card>
      <ReasonDialog open={!!rejectId} title="Reject quote?" body="The quote is marked rejected and the reason is logged to the audit trail." confirmLabel="Reject quote" danger onConfirm={reject} onCancel={() => setRejectId(null)} />
    </div>
  );
}

export default QuotesList;
